// Small shared pieces of the interface.
import { useEffect, useState, type ReactNode } from 'react';
import { useGame, fmt, money, clock, CLASS_INFO } from '../state.tsx';
import { ItemArt } from '../art/ItemArt.tsx';
import { Logo, Skyline, StatIcon } from '../art/Scenes.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { assetUrl } from '../art/assets.ts';
import { useAnimatedNumber, usePulse } from '../fx/fx.tsx';

// ---------------------------------------------------------------- routing
export function useRoute(): string[] {
  const read = () => (window.location.hash.replace(/^#\/?/, '') || 'home').split('/');
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => {
      setRoute(read());
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}
export const go = (path: string) => {
  window.location.hash = `#/${path}`;
};

// ---------------------------------------------------------------- header, stats, nav
export function Header() {
  const { content, me, user, logout } = useGame();
  return (
    <div className="header">
      <Skyline />
      <Logo name={content.gameName} />
      {me && (
        <div className="header-right">
          <div className="row" style={{ justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
            <div>
              <div className="who"><a onClick={() => go(`profile/${me.id}`)}>{me.name}</a></div>
              <div>Level {me.level} <span className="class-tag">{CLASS_INFO[me.charClass]?.name}</span></div>
              <div className="small">
                {user?.role === 'admin' && <><a onClick={() => go('admin')}>Admin</a> · </>}
                <a onClick={logout}>Log out</a>
              </div>
            </div>
            <a onClick={() => go(`profile/${me.id}`)}><Portrait seed={me.portrait} playerId={me.id} size={46} /></a>
          </div>
        </div>
      )}
    </div>
  );
}

/** Counts down to the next point of energy/stamina/health, purely for display. */
function useCountdown(ms: number, receivedAt: number): number {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return Math.max(0, ms - (Date.now() - receivedAt));
}

export function StatBar() {
  const { me, refresh } = useGame();
  const [receivedAt, setReceivedAt] = useState(Date.now());
  useEffect(() => setReceivedAt(Date.now()), [me]);
  const t = me?.timers ?? {};
  const e = useCountdown(t.energyNextMs ?? 0, receivedAt);
  const s = useCountdown(t.staminaNextMs ?? 0, receivedAt);
  const h = useCountdown(t.healthNextMs ?? 0, receivedAt);
  // When a timer runs out, ask the server for the new value.
  useEffect(() => {
    if (!me) return;
    const due = [t.energyNextMs, t.staminaNextMs, t.healthNextMs].filter((x: number) => x > 0);
    if (!due.length) return;
    const wait = Math.min(...due) + 400;
    const timer = setTimeout(refresh, wait);
    return () => clearTimeout(timer);
  }, [me, refresh]);
  const cash = useAnimatedNumber(me?.cash ?? 0);
  const xp = useAnimatedNumber(me?.xp ?? 0);
  const cashUp = usePulse(me?.cash);
  const levelUp = usePulse(me?.level, 2400);
  if (!me) return <div className="stats" />;
  const atCap = me.level >= (me.maxLevel ?? 100);
  const xpPct = atCap ? 100 : Math.min(100, ((me.xp - me.xpLevelStart) / Math.max(1, me.xpNextLevel - me.xpLevelStart)) * 100);
  return (
    <div className="stats">
      <div className="stats-main">
        <div className={`stat cash ${cashUp ? 'pulse' : ''}`}>
          <StatIcon kind="cash" size={18} />
          <div>
            <div className="label">Cash <a className="bank-link" onClick={() => go('bank')}>(Bank)</a></div>
            <div className="value">{money(cash)}</div>
            <div className="sub"><a onClick={() => go('bank')}>Bank: {money(me.bank)}</a></div>
          </div>
        </div>
        <div className="stat health">
          <StatIcon kind="health" size={18} />
          <div>
            <div className="label">Health</div>
            <div className="value">{me.health}/{me.maxHealth}</div>
            <div className="sub">{me.health < me.maxHealth ? <><a onClick={() => go('hospital')}>Heal</a> · +1 in {clock(h)}</> : 'Full'}</div>
          </div>
        </div>
        <div className={`stat energy ${me.energy >= me.maxEnergy ? 'full' : ''}`}>
          <StatIcon kind="energy" size={18} />
          <div>
            <div className="label">Energy</div>
            <div className="value">{me.energy}/{me.maxEnergy}</div>
            <div className="sub">{me.energy < me.maxEnergy ? <>More in {clock(e)}{me.sandwiches > 0 && me.sandwichesLeftToday > 0 && <> · <a onClick={() => go('jobs')} title="You have a Pastrami Sandwich: eat it on the Jobs page">Sandwich ×{me.sandwiches}</a></>}</> : 'Full'}</div>
          </div>
        </div>
        <div className="stat stamina">
          <StatIcon kind="stamina" size={18} />
          <div>
            <div className="label">Stamina</div>
            <div className="value">{me.stamina}/{me.maxStamina}</div>
            <div className="sub">{me.stamina < me.maxStamina ? `More in ${clock(s)}` : 'Full'}</div>
          </div>
        </div>
      </div>
      <div className={`stat level ${levelUp ? 'leveled' : ''}`}>
        <div className="xp-side">
          <div className="xp-label"><StatIcon kind="xp" /> Experience</div>
          <div className="xp-nums">{atCap ? <>Top level · {fmt(xp)}</> : <>{fmt(xp)} / {fmt(me.xpNextLevel)}</>}</div>
          <div className={`xpbar ${atCap ? 'capped' : ''}`}><div style={{ width: `${xpPct}%` }} /></div>
          {me.canPrestige && <div className="sub"><a onClick={() => go('prestige')} className="gold prestige-ready">Prestige now ›</a></div>}
          {me.skillPoints > 0 && <div className="sub"><a onClick={() => go(`profile/${me.id}`)} className="gold">{me.skillPoints} skill points to spend!</a></div>}
        </div>
        <div className="level-badge" title="Your level (click for Prestige)" onClick={() => go('prestige')} style={{ cursor: 'pointer' }}>
          <svg width="30" height="36" viewBox="0 0 30 36" aria-hidden="true"><path d="M15 1l13 5v11c0 9-6 15-13 18C8 32 2 26 2 17V6z" fill="#d9a520" stroke="#3a2a00" strokeWidth="1.2" /><path d="M15 5l9 3.5v8.5c0 6.5-4 11-9 13.5" fill="rgba(255,255,255,.25)" /></svg>
          <div className="label">Level <span className="level-num">{me.level}</span></div>
          {me.prestige > 0 && <a className="level-prestige" onClick={() => go('prestige')} title={`Prestige ${ROMAN[me.prestige]}`}><PrestigeStar rank={me.prestige} size={18} /></a>}
        </div>
      </div>
    </div>
  );
}

const TABS: [string, string][] = [
  ['home', 'Home'], ['jobs', 'Jobs'], ['fight', 'Fight'], ['territory', 'Territory'], ['inventory', 'Inventory'],
  ['mafia', 'My Mafia'], ['heists', 'Heists'], ['casino', 'Casino'], ['godfather', 'Godfather'], ['bank', 'Bank'], ['hospital', 'Hospital'], ['leaderboard', 'Top Mafia'],
];

export function Nav({ current }: { current: string }) {
  const { me } = useGame();
  return (
    <div className="nav">
      {TABS.map(([path, label]) => (
        <a key={path} className={current === path ? 'active' : ''} href={`#/${path}`}>
          {label}
          {path === 'mafia' && (me?.pendingInvites > 0 || me?.giftsReady > 0) && <span className="badge" title="Invitations and gifts to send">{(me.pendingInvites || 0) + (me.giftsReady || 0)}</span>}
          {path === 'heists' && me?.heistsOpen > 0 && current !== 'heists' && <span className="badge" title="Heists you can join">{me.heistsOpen}</span>}
          {path === 'home' && me?.unreadUpdates > 0 && current !== 'home' && <span className="badge">{me.unreadUpdates}</span>}
        </a>
      ))}
    </div>
  );
}

export function FlashBox() {
  const { flash, setFlash } = useGame();
  if (!flash) return null;
  return (
    <div className={`flash ${flash.kind}`}>
      <div>
        {flash.title && <div className="title">{flash.title}</div>}
        <div>{flash.text}</div>
      </div>
      <span className="close" onClick={() => setFlash(null)}>✕</span>
    </div>
  );
}

// ---------------------------------------------------------------- bits
export function Panel({ title, children, right }: { title: ReactNode; children: ReactNode; right?: ReactNode }) {
  return (
    <div className="panel">
      <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{title}</span>
        {right}
      </div>
      <div className="panel-body">{children}</div>
    </div>
  );
}

// v0.9 prestige: a gold star with the rank in Roman numerals.
export const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export function PrestigeStar({ rank, size = 16 }: { rank: number; size?: number }) {
  if (!rank) return null;
  return (
    <svg className="prestige-star" width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={`Prestige ${ROMAN[rank]}`}>
      <title>{`Prestige ${ROMAN[rank]}`}</title>
      <path d="M12 1.5l3.1 6.6 7.2.9-5.3 5 1.4 7.1L12 17.6l-6.4 3.5L7 14l-5.3-5 7.2-.9z" fill={rank >= 10 ? '#ff5544' : '#e8b923'} stroke="#3a2a00" strokeWidth="1" />
      <text x="12" y="14.6" textAnchor="middle" fontSize={rank >= 4 && rank !== 5 && rank !== 10 ? 5.2 : 6.5} fontWeight="bold" fontFamily="Georgia, serif" fill="#2a1a00">{ROMAN[rank]}</text>
    </svg>
  );
}

export function PlayerLink({ id, name }: { id: number | null; name: string | null }) {
  if (!id) return <span>{name ?? 'someone'}</span>;
  return <a onClick={() => go(`profile/${id}`)}>{name}</a>;
}

/** Renders event text, turning {actor} and {target} into player links. */
export function EventText({ e }: { e: any }) {
  const parts = String(e.text).split(/(\{actor\}|\{target\})/);
  return (
    <span>
      {parts.map((p: string, i: number) =>
        p === '{actor}' ? <PlayerLink key={i} id={e.actorId} name={e.actorName} /> : p === '{target}' ? <PlayerLink key={i} id={e.targetId} name={e.targetName} /> : <span key={i}>{p}</span>,
      )}
    </span>
  );
}

/** v0.7: the money an event moved for you (+/−), read from its data. */
function eventAmount(e: any, d: any): number | null {
  if (!d) return null;
  switch (e.kind) {
    case 'attacked': return d.cash ? (d.won ? d.cash : -d.cash) : null;
    case 'robbed': return d.cash || null;
    case 'hitlist': return d.bounty ?? null;
    case 'race': case 'cboss': case 'casino': return d.cash ?? null;
    case 'sidebet': return d.amount ?? null;
    case 'gift_cash': case 'auction_outbid': case 'auction_won': case 'fight_streak': return d.cash ?? null;
    default: return typeof d.cash === 'number' ? d.cash : null;
  }
}

/** Where a click on an event should take you. */
function eventLink(e: any, d: any): [string, string] | null {
  switch (e.kind) {
    case 'attacked': case 'revenge': return e.actorId ? [`profile/${e.actorId}`, 'Attack back'] : null;
    case 'robbed': return e.actorId ? [`profile/${e.actorId}`, 'Pay them a visit'] : null;
    case 'invite': return ['mafia', 'Respond'];
    case 'siege': case 'siege_help': return d?.boroughId ? [`territory/siege-${d.boroughId}`, 'To the map'] : ['territory', 'To the map'];
    case 'turf_lost': case 'turf_held': case 'turf': return d?.battleId ? [`territory/battle-${d.battleId}`, 'Watch the battle'] : ['territory', 'To the map'];
    case 'sidebet': return ['sidebets', 'Side bets'];
    case 'race': return ['races', 'The track'];
    case 'cboss': return ['fight/enemy', 'Public Enemy'];
    case 'rival': return ['fight', 'Rivals'];
    case 'hitlist': return ['fight/hitlist', 'Hitlist'];
    case 'heist_invite': case 'heist': return ['heists', 'Heists'];
    case 'mention': return ['chat', 'Chat'];
    case 'trade': return ['inventory/trades', 'Trades'];
    case 'gift_item': return ['inventory/items', 'Inventory'];
    case 'auction': case 'auction_outbid': case 'auction_won': return ['inventory/auction', 'Auction House'];
    case 'title': return ['customize', 'Wear it'];
    case 'statue': return ['territory', 'See it'];
    case 'prestige': case 'max_level': case 'city_choice': return ['prestige', 'Prestige'];
    case 'fight_streak': case 'streak_end': return ['fight', 'Fight'];
    case 'wall': case 'like': return null;
    default: return null;
  }
}

/** One line of a news feed: a mark for the kind, the text with links, the cash it moved and a shortcut. */
export function FeedLine({ e, when }: { e: any; when: string }) {
  let d: any = null;
  try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data ?? null; } catch { /* old rows */ }
  const amount = eventAmount(e, d);
  const link = eventLink(e, d);
  return (
    <div className={`feed-item kind-${e.kind}`}>
      <span className={`feed-dot ${e.kind}`} />
      <div className="grow">
        <EventText e={e} />
        {link && <> <a className="feed-go" onClick={() => go(link[0])}>{link[1]} ›</a></>}
      </div>
      {amount !== null && amount !== 0 && <span className={`feed-amt ${amount > 0 ? 'up' : 'down'}`}>{amount > 0 ? '+' : '−'}${Math.abs(amount).toLocaleString('en-US')}</span>}
      <span className="when">{when}</span>
    </div>
  );
}

export function ItemIcon({ itemId, size = 40 }: { itemId: string; size?: number }) {
  const { byId } = useGame();
  const item = byId.items.get(itemId);
  const supplied = assetUrl(`items/${itemId}`);
  if (supplied) return <img className="item-icon" src={supplied} width={size} height={size} alt={item?.name ?? ''} />;
  return <ItemArt icon={item?.icon ?? 'briefcase'} seed={itemId} size={size} rarity={item?.rarity} />;
}

export function ItemName({ itemId }: { itemId: string }) {
  const { byId } = useGame();
  const item = byId.items.get(itemId);
  return <span className={`rarity-${item?.rarity ?? 'common'}`} style={{ fontWeight: 'bold' }}>{item?.name ?? itemId}</span>;
}

export function Medals({ level }: { level: number }) {
  return (
    <span className="medals" title={`Mastery level ${level} of 3`}>
      <span className={`medal ${level >= 1 ? 'bronze' : 'off'}`} />
      <span className={`medal ${level >= 2 ? 'silver' : 'off'}`} />
      <span className={`medal ${level >= 3 ? 'gold' : 'off'}`} />
    </span>
  );
}

export function MasteryBar({ pct }: { pct: number }) {
  return <span className="mastery"><div style={{ width: `${pct}%` }} /></span>;
}

export function Loading() {
  return <div className="spinner">Loading…</div>;
}

/** Fetch data for a page and reload when deps change. */
export function useLoad<T = any>(fn: () => Promise<T>, deps: unknown[], refreshMs = 0): { data: T | null; reload: () => void; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  // Optional automatic refresh (used by the news feeds).
  useEffect(() => {
    if (!refreshMs) return;
    const t = setInterval(() => setN((x) => x + 1), refreshMs);
    return () => clearInterval(t);
  }, [refreshMs]);
  useEffect(() => {
    let alive = true;
    fn().then((d) => alive && (setData(d), setError(null))).catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { data, error, reload: () => setN((x) => x + 1) };
}
